/**
 * @module Detached overlay state: a value that a large view sets, but that only a small child
 * component owns and renders. Opening an editor from a 100-card board then re-renders the editor,
 * not the board. See docs/developer/ui-latency.md for the measured cost this removes.
 */

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/** Stable handle a view keeps to set a detached value without re-rendering itself. */
export interface DetachedState<T> {
  /**
   * Replace the value held by the mounted `Detached` slot. Stable for the life of the view. A call
   * before the slot mounts or after it unmounts is ignored, because nothing displays the value.
   */
  set: (value: T) => void;
  /** Setter of the mounted slot, or null while none is mounted. Written only by `Detached`. */
  slot: { /** Active slot setter, cleared when its component unmounts. */ current: ((value: T) => void) | null };
}

/**
 * Create the stable handle for one detached value. Call it in the view that triggers the change
 * (for example, the board whose cards open the editor) and pass it to exactly one `Detached`.
 */
export function useDetachedState<T>(): DetachedState<T> {
  const handle = useRef<DetachedState<T> | null>(null);
  if (handle.current === null) {
    const slot: DetachedState<T>['slot'] = { current: null };
    handle.current = { slot, set: value => slot.current?.(value) };
  }
  return handle.current;
}

/** Inputs of the slot that owns a detached value. */
interface DetachedProps<T> {
  /** Handle created by `useDetachedState` in the owning view. */
  state: DetachedState<T>;
  /** Value shown before the first `set` call. */
  initial: T;
  /** Render the current value; re-runs when the value changes or the owning view re-renders. */
  children: (value: T) => ReactNode;
}

/**
 * Own a detached value and render it. Setting the value re-renders only this component and its
 * children; the view that called `state.set` is not re-rendered.
 */
export function Detached<T>({ state, initial, children }: DetachedProps<T>) {
  const [value, setValue] = useState<T>(initial);
  useLayoutEffect(() => {
    const slot = state.slot;
    slot.current = setValue;
    return () => {
      if (slot.current === setValue) slot.current = null;
    };
  }, [state]);
  return <>{children(value)}</>;
}
